import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ActionIcon, Badge, Button, Card, Flex, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { LocationOnOutlined, SaveOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useGetPostalCodeQuery } from "../../graphql/graphql";
import { titleCase } from "../../utils/String.utils";
import { Length } from "../../utils/Validation.utils";
import { ActionsMenu } from "../../shared/ActionsMenu";
import { MapLink } from "../../shared/MapLink";
import { showErrorNotification } from "../../shared/Notifications";
export const LocationCard = ({ isNew = false, location, onSave, onCancel, onDeactivate, onDelete }) => {
    // State
    const [editing, setEditing] = useState(isNew);
    const [saving, setSaving] = useState(false);
    const zipRef = useRef(null);
    // Form
    const form = useForm({
        initialValues: {
            name: location?.name ?? "",
            address1: location?.address1 ?? "",
            address2: location?.address2 ?? "",
            city: location?.city ?? "",
            stateName: location?.stateName ?? null,
            postalCode: location?.postalCode ?? ""
        }
    });
    // Effects
    useEffect(() => {
        if (location) {
            form.setValues({
                name: location.name ?? "",
                address1: location.address1 ?? "",
                address2: location.address2 ?? "",
                city: location.city ?? "",
                stateName: location.stateName ?? null,
                postalCode: location.postalCode ?? ""
            });
            form.resetDirty();
        }
    }, [location]);
    // Auto-fill city/state from postal code
    const [{ data: postalCodeData }] = useGetPostalCodeQuery({
        variables: { postalCode: form.values.postalCode || "" },
        pause: form.values.postalCode.length < Length.PostalCode
    });
    useEffect(() => {
        if (!editing)
            return;
        if (postalCodeData?.postalCodes?.postalCode) {
            form.setValues({
                ...form.values,
                city: postalCodeData.postalCodes.postalCode.city || "",
                stateName: postalCodeData.postalCodes.postalCode.stateName || ""
            });
        }
    }, [postalCodeData]);
    // Handlers
    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(form.values);
            setEditing(false);
            form.resetDirty();
        }
        catch {
            showErrorNotification("Failed to save location");
        }
        finally {
            setSaving(false);
        }
    };
    const handleCancel = () => {
        if (isNew) {
            onCancel?.();
        }
        else {
            form.setValues({
                name: location?.name ?? "",
                address1: location?.address1 ?? "",
                address2: location?.address2 ?? "",
                city: location?.city ?? "",
                stateName: location?.stateName ?? null,
                postalCode: location?.postalCode ?? ""
            });
            form.resetDirty();
            setEditing(false);
        }
    };
    const enterEditMode = () => {
        if (!editing)
            setEditing(true);
    };
    // Data
    const addressForMap = [
        form.values.address1,
        form.values.address2,
        form.values.city,
        form.values.stateName,
        form.values.postalCode
    ]
        .filter(Boolean)
        .join(" ");
    const isActive = location?.isActive ?? true;
    const isDirty = form.isDirty();
    const showSaveCancel = editing && isDirty;
    return (_jsx(Card, { withBorder: true, radius: "md", padding: "sm", style: {
            cursor: editing ? "default" : "pointer",
            opacity: isActive ? 1 : 0.6,
            ...(editing && {
                borderColor: "var(--mantine-color-primary-3)",
                borderWidth: 2
            })
        }, onClick: enterEditMode, children: _jsxs(Flex, { align: "start", justify: "space-between", children: [_jsx(MapLink, { address: addressForMap, children: _jsx(ActionIcon, { variant: "light", color: "primary", size: "xl", radius: "md", onClick: (e) => e.stopPropagation(), children: _jsx(LocationOnOutlined, { style: { fontSize: "1.5rem" } }) }) }), _jsxs(Stack, { gap: 0, style: { flex: 0.4 }, children: [_jsx(TextInput, { variant: "unstyled", placeholder: "Location name", autoFocus: isNew, readOnly: !editing, onClick: enterEditMode, styles: {
                                input: { textAlign: "left" }
                            }, ...form.getInputProps("name") }), !isActive && (_jsx(Badge, { size: "xs", color: "gray", variant: "light", children: "Inactive" }))] }), _jsxs(Stack, { gap: 0, style: { flex: 0.5 }, children: [_jsx(TextInput, { variant: "unstyled", placeholder: "Address", readOnly: !editing, onClick: enterEditMode, styles: {
                                input: { textAlign: "left", fontWeight: 500 }
                            }, ...form.getInputProps("address1") }), editing && (_jsx(TextInput, { variant: "unstyled", placeholder: "Line 2", styles: {
                                input: { textAlign: "left", fontWeight: 500 }
                            }, ...form.getInputProps("address2") })), _jsxs(Flex, { gap: "xs", align: "center", style: { cursor: editing ? "text" : "pointer" }, onClick: () => {
                                enterEditMode();
                                // Focus zip input after entering edit mode
                                setTimeout(() => zipRef.current?.focus(), 0);
                            }, children: [_jsxs(Text, { size: "sm", c: "dimmed", children: [titleCase(form.values.city), form.values.city && form.values.stateName
                                            ? ", "
                                            : "", titleCase(form.values.stateName)] }), editing ? (_jsx(TextInput, { ref: zipRef, variant: "unstyled", placeholder: "Zip", maxLength: 5, styles: {
                                        input: {
                                            textAlign: "left",
                                            fontWeight: 400,
                                            fontSize: "0.85rem",
                                            color: "var(--mantine-color-gray-6)",
                                            width: "3.5rem"
                                        }
                                    }, onClick: (e) => e.stopPropagation(), ...form.getInputProps("postalCode") })) : (_jsx(Text, { size: "sm", c: "dimmed", children: form.values.postalCode }))] })] }), _jsx(Flex, { gap: 4, align: "center", onClick: (e) => e.stopPropagation(), style: { flexShrink: 0 }, children: showSaveCancel ? (_jsxs(_Fragment, { children: [_jsx(Button, { size: "compact-xs", leftSection: _jsx(SvgIcon, { component: SaveOutlined }), loading: saving, onClick: handleSave, disabled: !form.values.name, children: "Save" }), _jsx(Button, { size: "compact-xs", variant: "subtle", c: "dimmed", onClick: handleCancel, children: "Cancel" })] })) : (!isNew && (_jsx(ActionsMenu, { entityId: location?.locationId, onClickEdit: enterEditMode, isActive: isActive, allowEdit: true, allowDeactivate: true, allowDelete: true, handleActiveStatus: onDeactivate, handleDelete: onDelete }))) })] }) }));
};
